import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';

interface TemplateFormData {
  name: string;
  subject: string;
  body: string;
}

interface EmailTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: {
    id: string;
    name: string;
    subject: string;
    body: string;
    variables: string[];
    isActive: boolean;
  } | null;
  onSuccess?: () => void;
}

export function EmailTemplateModal({
  open,
  onOpenChange,
  template,
  onSuccess,
}: EmailTemplateModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [variables, setVariables] = useState<string[]>([]);
  const [newVariable, setNewVariable] = useState('');

  const isEditing = !!template;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TemplateFormData>();

  useEffect(() => {
    if (template) {
      reset({
        name: template.name,
        subject: template.subject,
        body: template.body,
      });
      setVariables(template.variables || []);
    } else {
      reset({
        name: '',
        subject: '',
        body: '',
      });
      setVariables([]);
    }
  }, [template, reset]);

  const createMutation = trpc.settings.createEmailTemplate.useMutation({
    onSuccess: () => {
      reset();
      setVariables([]);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.settings.updateEmailTemplate.useMutation({
    onSuccess: () => {
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: TemplateFormData) => {
    setIsSubmitting(true);
    if (isEditing && template) {
      updateMutation.mutate({
        id: template.id,
        name: data.name,
        subject: data.subject,
        body: data.body,
        variables,
      });
    } else {
      createMutation.mutate({
        name: data.name,
        subject: data.subject,
        body: data.body,
        variables,
      });
    }
  };

  const addVariable = () => {
    const trimmed = newVariable.trim().replace(/\s+/g, '_');
    if (trimmed && !variables.includes(trimmed)) {
      setVariables([...variables, trimmed]);
      setNewVariable('');
    }
  };

  const removeVariable = (variable: string) => {
    setVariables(variables.filter((v) => v !== variable));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Template' : 'Create Template'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update this email template'
              : 'Create a new email template for automated notifications'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Template Name *</Label>
            <Input
              id="name"
              {...register('name', { required: 'Template name is required' })}
              placeholder="e.g., booking_confirmation"
            />
            <p className="text-xs text-muted-foreground">
              Use lowercase with underscores (e.g., payment_received)
            </p>
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Email Subject *</Label>
            <Input
              id="subject"
              {...register('subject', { required: 'Subject is required' })}
              placeholder="e.g., Booking Confirmed - {{bookingReference}}"
            />
            {errors.subject && (
              <p className="text-sm text-red-500">{errors.subject.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Email Body *</Label>
            <Textarea
              id="body"
              {...register('body', { required: 'Body is required' })}
              placeholder="Dear {{customerName}},&#10;&#10;Your booking has been confirmed...&#10;&#10;Best regards,&#10;{{companyName}}"
              rows={12}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Use {`{{variableName}}`} syntax for dynamic content
            </p>
            {errors.body && (
              <p className="text-sm text-red-500">{errors.body.message}</p>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Template Variables</Label>
                <p className="text-xs text-muted-foreground">
                  Define variables that can be used in subject and body
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                placeholder="e.g., customerName"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addVariable();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addVariable}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {variables.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {variables.map((variable) => (
                  <span
                    key={variable}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                  >
                    <span className="font-mono">{`{{${variable}}}`}</span>
                    <button
                      type="button"
                      onClick={() => removeVariable(variable)}
                      className="hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {(createMutation.error || updateMutation.error) && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error?.message || updateMutation.error?.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
